import { useEffect, useMemo, useState } from "react";
import { useParticipants } from "@livekit/components-react";
import type {
  LocalParticipant,
  Participant,
  TrackPublication,
} from "livekit-client";
import { Track } from "livekit-client";
import { noop } from "lodash";
import type { Peer } from "@/react/types/Live/Peer";
import { selectSettings, useLiveStore } from "../../store";
import { ACTION_TYPES } from "./helpers";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";

const useAudioMutedState = (
  participant: Participant | undefined,
  isAudioStatusMuted: boolean,
) => {
  const [isAudioMuted, setIsAudioMuted] = useState(
    !participant?.isMicrophoneEnabled || isAudioStatusMuted,
  );

  useEffect(() => {
    const onMuted = (trackPublication: TrackPublication) => {
      if (trackPublication.kind === Track.Kind.Audio) {
        setIsAudioMuted(true);
      }
    };
    const onUnmuted = (trackPublication: TrackPublication) => {
      if (trackPublication.kind === Track.Kind.Audio) {
        setIsAudioMuted(false);
      }
    };

    if (participant) {
      participant.on("trackMuted", onMuted);
      participant.on("trackUnmuted", onUnmuted);
    }

    return () => {
      if (participant) {
        participant.off("trackMuted", onMuted);
        participant.off("trackUnmuted", onUnmuted);
      }
    };
  }, [participant]);

  return isAudioMuted;
};

const useMuteMicrophone = (localParticipant: LocalParticipant | null) => () => {
  if (!localParticipant) {
    return Promise.reject("localParticipant is not present");
  }
  const encoder = new TextEncoder();
  const action = JSON.stringify({
    type: ACTION_TYPES.MUTE_MICROPHONE,
  });

  return localParticipant.publishData(encoder.encode(action), {
    reliable: false,
  });
};

export const useLivekitPeerMediaStatus = (peer: Peer) => {
  const localParticipant = useLivekitLocalParticipant();
  const participants = useParticipants();
  const participant = useMemo(
    () => participants.find(p => p.sid === peer.id),
    [participants, peer.id],
  );

  const { microphoneEnabled: isMicrophoneEnabled } =
    useLiveStore(selectSettings);
  const isLocal = participant?.isLocal;
  const isAudioStatusMuted = isLocal ? !isMicrophoneEnabled : false;

  const isAudioMuted = useAudioMutedState(participant, isAudioStatusMuted);
  const muteMicrophone = useMuteMicrophone(localParticipant);

  return {
    isMicrophoneEnabled: !isAudioMuted,
    muteMicrophone,
    isCameraEnabled: true,
    disableCamera: noop,
  };
};
